import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { VirtualAdminTable, type VirtualColumnDef } from "@/components/ui/virtual-table";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { BlogPostCards } from "@/components/blog/BlogPostCards";
import { STACKED_CARDS_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { toAdminBlogDetailPath } from "@/lib/routes/admin-route-catalog";
import { listBlogClusters } from "@/services/blog";
import {
  coverageLabel,
  describeLive,
  describeReadiness,
  describeStatus,
  summariseClusterList,
  summariseMissingLocales,
} from "@/lib/blog/list-view";
import { LTR_ISOLATE_ATTRS } from "@/lib/blog/locale-options";
import { classifyRequestFailure, describeFailure } from "@/lib/blog/request-failure";
import type { BlogClusterRow } from "@/lib/blog/types";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;
const LIST_ENDPOINT = "GET /v1/admin/blog/posts";

const BLOG_COLUMNS: VirtualColumnDef[] = [
  { key: "slug", header: "Slug", width: "1.6fr" },
  { key: "status", header: "Status", width: "0.7fr" },
  { key: "title", header: "Title (en)", width: "1.8fr" },
  { key: "locales", header: "Locales", width: "1.3fr" },
  { key: "readiness", header: "Readiness", width: "1.2fr" },
  { key: "live", header: "Live", width: "1.4fr" },
  { key: "modified", header: "Last modified", width: "1fr" },
];

function LoadingRows() {
  // The header, the filters and the column widths are already final, so nothing
  // reflows when the data lands (M1-S1).
  return (
    <div className="rounded-md border" data-testid="blog-list-skeleton">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="flex items-center gap-4 border-b p-4 last:border-b-0">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

function EmptyPanel() {
  return (
    <div className="rounded-md border p-8 text-center" data-testid="blog-list-empty">
      <h3 className="text-lg font-semibold">No posts yet</h3>
      <p className="mx-auto mt-2 max-w-prose text-sm text-muted-foreground">
        Blog posts are written by the <code className="font-mono">atsr-blog</code> agent through the
        authoring tool API. They are not created in this dashboard. As soon as the agent writes one,
        the cluster shows up here as a draft you can review, correct and publish.
      </p>
    </div>
  );
}

function MissingLocales({ row }: { row: BlogClusterRow }) {
  const missing = summariseMissingLocales(row.missingLocales);
  if (missing.all.length === 0) {
    return <span className="text-xs text-muted-foreground">complete</span>;
  }
  return (
    <span className="text-xs text-muted-foreground" title={`Missing: ${missing.all.join(", ")}`}>
      <span className="font-mono" {...LTR_ISOLATE_ATTRS}>
        {missing.inline.join(" ")}
      </span>
      {missing.overflow > 0 && <span className="ml-1">{missing.overflowLabel}</span>}
    </span>
  );
}

export default function BlogPosts() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const stacked = useMediaQuery(STACKED_CARDS_QUERY);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin-blog-posts", page],
    queryFn: () => listBlogClusters({ page, pageSize: PAGE_SIZE }),
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openPost = (row: BlogClusterRow) => {
    void navigate(toAdminBlogDetailPath(row.slug));
  };

  const renderRow = (row: BlogClusterRow) => {
    const status = describeStatus(row.status);
    const readiness = describeReadiness(row.readiness);
    const live = describeLive(row);

    return (
      <>
        <div role="cell" className="p-4 align-middle">
          <span className="font-mono text-xs" {...LTR_ISOLATE_ATTRS}>
            {row.slug}
          </span>
        </div>
        <div role="cell" className="p-4 align-middle">
          <Badge variant={status.variant} className={cn(row.status === "archived" && "opacity-70")}>
            {status.label}
          </Badge>
        </div>
        <div role="cell" className="p-4 align-middle">
          {row.title}
        </div>
        <div role="cell" className="p-4 align-middle">
          <div className="tabular-nums">{coverageLabel(row)}</div>
          <MissingLocales row={row} />
        </div>
        <div role="cell" className="p-4 align-middle">
          <Badge variant={readiness.variant}>{readiness.label}</Badge>
        </div>
        <div role="cell" className="p-4 align-middle">
          <div>{live.live}</div>
          {live.hasDrift ? (
            <div className="text-xs text-warning-foreground">{live.drift}</div>
          ) : (
            <div className="text-xs text-muted-foreground">{live.detail}</div>
          )}
        </div>
        <div role="cell" className="p-4 align-middle text-muted-foreground">
          {row.updatedAt.slice(0, 10)}
        </div>
      </>
    );
  };

  const header = (
    <AdminPageHeader
      title="Blog"
      description="Posts authored by the atsr-blog agent, reviewed and corrected here before they ship to readers."
      actions={
        <Button variant="outline" onClick={() => void refetch()}>
          Refresh
        </Button>
      }
    />
  );

  if (isError) {
    const failure = classifyRequestFailure(error);
    return (
      <div className="space-y-6">
        {header}
        <AdminSectionCard
          title="All posts"
          description={
            failure.kind === "forbidden" ? "Access refused by the server" : "Post list unavailable"
          }
        >
          <div
            className="rounded-md border p-8"
            data-testid={failure.kind === "forbidden" ? "blog-list-forbidden" : "blog-list-error"}
          >
            <h3 className="text-lg font-semibold">
              {failure.kind === "forbidden"
                ? "Your role cannot view the blog"
                : "Could not load the post list"}
            </h3>
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">
              {describeFailure(failure, LIST_ENDPOINT)}
            </p>
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">{failure.message}</p>
            {failure.kind === "failed" && (
              <Button className="mt-4" onClick={() => void refetch()}>
                Retry request
              </Button>
            )}
          </div>
        </AdminSectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      <AdminSectionCard
        title="All posts"
        description={isLoading ? "Loading post clusters…" : summariseClusterList(rows)}
      >
        {isLoading ? (
          <LoadingRows />
        ) : rows.length === 0 ? (
          <EmptyPanel />
        ) : stacked ? (
          <BlogPostCards rows={rows} onSelect={openPost} />
        ) : (
          <VirtualAdminTable
            data={rows}
            columns={BLOG_COLUMNS}
            renderRow={renderRow}
            onRowClick={openPost}
            rowClassName="cursor-pointer"
            estimateSize={() => 72}
            emptyMessage="No posts found"
          />
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((current) => Math.min(totalPages, current + 1));
                }}
                disabled={page === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </AdminSectionCard>
    </div>
  );
}
